import React, { useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import { Wrench, Briefcase, Heart, Car, Sparkle, CheckCircle, XCircle, Storefront } from "@phosphor-icons/react";

// Static internal mockup page — no backend, no DB.
// Purpose: let the founder sign off on Card Types positioning + activation
// copy + per-type feature breakdown BEFORE we wire it up.
// Route: /pitch/card-types

const TYPES = [
  {
    id: "hustle",
    icon: Storefront,
    title: "Hustle",
    tag: "Car guards · Petrol attendants · Waiters · Street vendors",
    headline: "Get tipped. Get found. Get hired again.",
    colour: "#FFCC00",
    textColour: "#000",
    features: {
      "Quick-Pay chips (R5–R40)": true,
      "Send money (PayShap)": true,
      "Profile photo + skills": true,
      "Bio (short, 80 char)": true,
      "Social links": false,
      "Document portfolio": false,
      "Asset QR stickers": false,
      "Book a meeting CTA": false,
    },
    pitch: "Bibi at the car wash gets R20 tips on her phone before the customer drives off. No card machine. No fees.",
  },
  {
    id: "pro",
    icon: Wrench,
    title: "Pro",
    tag: "Plumbers · Electricians · Tradies · Mobile services",
    headline: "Skills + payment + repeat callbacks.",
    colour: "#000",
    textColour: "#fff",
    features: {
      "Quick-Pay chips (R5–R40)": "optional",
      "Send money (PayShap)": true,
      "Profile photo + skills": true,
      "Bio (medium, 240 char)": true,
      "Social links": true,
      "Document portfolio": true,
      "Asset QR stickers": true,
      "Book a meeting CTA": false,
    },
    pitch: "Sipho installs a geyser, slaps a Pro asset QR sticker on it. 18 months later it leaks — the customer scans, gets routed straight to Sipho. Free repeat work, forever.",
  },
  {
    id: "business",
    icon: Briefcase,
    title: "Business",
    tag: "Architects · Agents · Consultants · Lawyers · Reps",
    headline: "Portfolio first. Pay buttons hidden.",
    colour: "#0A2540",
    textColour: "#fff",
    features: {
      "Quick-Pay chips (R5–R40)": false,
      "Send money (PayShap)": "off by default",
      "Profile photo + skills": true,
      "Bio (long, 600 char)": true,
      "Social links": true,
      "Document portfolio": true,
      "Asset QR stickers": true,
      "Book a meeting CTA": true,
    },
    pitch: "Thandi the estate agent gives her card at a viewing. The scanner sees credentials, portfolio, a 'Book a viewing' button — not a R5 tip jar. Every listing gets its own Asset QR sticker that routes leads back to her dashboard.",
  },
  {
    id: "specialty",
    icon: Heart,
    title: "Specialty",
    tag: "Medical alert · Vehicle disc · Pet ID · Kid backup",
    headline: "One job. Done in 2 seconds.",
    colour: "#DC2626",
    textColour: "#fff",
    features: {
      "Quick-Pay chips (R5–R40)": false,
      "Send money (PayShap)": false,
      "Profile photo + skills": "type-specific",
      "Bio (long, 600 char)": "type-specific",
      "Social links": false,
      "Document portfolio": false,
      "Asset QR stickers": false,
      "Book a meeting CTA": false,
    },
    pitch: "Med card → allergies + emergency contact. Disc card → owner + VIN + insurance. Pet card → owner phone + vet. Single-purpose, ruthlessly simple.",
  },
];

const FeatureCell = ({ value }) => {
  if (value === true) return <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle weight="fill" size={14}/> Yes</span>;
  if (value === false) return <span className="inline-flex items-center gap-1 text-neutral-400"><XCircle weight="fill" size={14}/> Hidden</span>;
  return <span className="text-neutral-700 text-xs">{value}</span>;
};

const TypeCard = ({ t, selected, onSelect }) => {
  const Icon = t.icon;
  return (
    <button
      onClick={() => onSelect(t.id)}
      className={`text-left border-2 border-black p-4 brutal-btn transition-transform ${selected ? "translate-x-[-2px] translate-y-[-2px] shadow-[6px_6px_0_0_#000]" : ""}`}
      style={{ background: t.colour, color: t.textColour }}
      data-testid={`pitch-type-${t.id}`}
    >
      <Icon size={28} weight="bold" />
      <div className="font-display font-black text-2xl mt-2 leading-none">{t.title}</div>
      <div className="label-tag mt-2 opacity-80">{t.tag}</div>
      <div className="text-sm mt-3 font-bold leading-snug">{t.headline}</div>
    </button>
  );
};

const ActivationStep = ({ t }) => (
  <div className="border-2 border-black p-4 bg-white">
    <div className="label-tag text-neutral-500 mb-2">ACTIVATION STEP 1 (NEW)</div>
    <div className="font-display font-black text-2xl mb-1 leading-tight">What kind of card is this?</div>
    <div className="text-sm text-neutral-700 mb-4">We'll set up your profile differently based on what you do. You can change this later.</div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {TYPES.map((x) => {
        const Icon = x.icon;
        const active = x.id === t.id;
        return (
          <div key={x.id} className={`border-2 border-black p-2 text-center ${active ? "bg-[color:var(--cc-yellow)]" : "bg-white"}`}>
            <Icon size={20} weight="bold" />
            <div className="font-display font-black text-sm mt-1">{x.title}</div>
            <div className="text-[10px] text-neutral-600 leading-tight mt-1">{x.tag.split("·")[0].trim()}</div>
          </div>
        );
      })}
    </div>
    <div className="label-tag text-neutral-500 mt-3">Selected: {t.title}</div>
  </div>
);

const FeatureMatrix = ({ t }) => (
  <div className="border-2 border-black bg-white">
    <div className="bg-black text-white px-4 py-2 label-tag">FEATURE MATRIX — {t.title.toUpperCase()}</div>
    <table className="w-full text-sm">
      <tbody>
        {Object.entries(t.features).map(([k, v], i) => (
          <tr key={k} className={i % 2 ? "bg-[color:var(--cc-bg-2)]" : "bg-white"}>
            <td className="px-3 py-2">{k}</td>
            <td className="px-3 py-2 text-right"><FeatureCell value={v} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ProfileMockup = ({ t }) => {
  // Tiny visual approximation of what a scanned card profile looks like
  // per type. Hand-drawn boxes, not real data.
  const showPay = t.features["Send money (PayShap)"] === true;
  const showTips = t.features["Quick-Pay chips (R5–R40)"] === true;
  const showPortfolio = t.features["Document portfolio"] === true;
  const showBook = t.features["Book a meeting CTA"] === true;
  const showAssets = t.features["Asset QR stickers"] && t.features["Asset QR stickers"] !== false;

  return (
    <div className="border-2 border-black bg-white max-w-sm mx-auto">
      <div className="bg-black text-white px-4 py-3 label-tag">PUBLIC PROFILE PREVIEW · {t.title.toUpperCase()}</div>
      <div className="p-4 space-y-3 text-sm">
        <div className="w-20 h-20 bg-[color:var(--cc-bg-2)] border border-black" />
        <div className="font-display font-black text-xl">Sample Name</div>
        <div className="label-tag text-neutral-500">Cape Town</div>
        <div className="flex flex-wrap gap-1">
          <span className="px-2 py-0.5 bg-black text-white text-xs">SKILL</span>
          <span className="px-2 py-0.5 bg-black text-white text-xs">SKILL</span>
          {t.id !== "hustle" && t.id !== "specialty" && (
            <span className="px-2 py-0.5 bg-black text-white text-xs">SKILL</span>
          )}
        </div>
        <div className="h-px bg-black/20" />

        {showBook && (
          <div className="border-2 border-black px-3 py-3 bg-[color:var(--cc-yellow)] label-tag text-center">📅 BOOK A MEETING</div>
        )}

        <div className="border-2 border-black px-3 py-3 bg-white label-tag text-center">📞 REQUEST CONTACT NUMBER</div>

        {showPay && (
          <div className="border-2 border-black px-3 py-3 bg-[color:var(--cc-yellow)] label-tag text-center">💸 SEND {t.title.toUpperCase()} MONEY (PAYSHAP)</div>
        )}

        {showTips && (
          <div className="border-2 border-black p-2 bg-[color:var(--cc-yellow)]">
            <div className="label-tag mb-1">PAY {t.title.toUpperCase()}</div>
            <div className="grid grid-cols-5 gap-1">
              {[5, 10, 20, 30, 40].map((n) => (
                <div key={n} className="border border-black bg-white text-center text-xs py-1">R{n}</div>
              ))}
            </div>
          </div>
        )}

        {showPortfolio && (
          <div className="border-2 border-black px-3 py-3 bg-white label-tag">📎 PORTFOLIO · LINKEDIN · DOCS</div>
        )}

        {showAssets && (
          <div className="border-2 border-black px-3 py-3 bg-white label-tag">🔖 MY ASSET QRs ({t.features["Asset QR stickers"]})</div>
        )}

        <div className="border-2 border-black px-3 py-3 bg-green-500 label-tag text-center">SHARE ON WHATSAPP</div>
      </div>
    </div>
  );
};

export default function PitchCardTypes() {
  const [active, setActive] = useState("business");
  const t = TYPES.find((x) => x.id === active) || TYPES[0];

  return (
    <div className="min-h-screen bg-[color:var(--cc-bg)]">
      <Nav />
      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <div className="label-tag text-neutral-500 mb-2">INTERNAL · PITCH MOCKUP · NOT LIVE</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
          One card<br/>shouldn't fit<br/>everyone.
        </h1>
        <p className="mt-4 text-base text-neutral-700 max-w-2xl">
          Right now every Curb Crew card shows the same thing: tip chips, "send money", service-provider framing. That's perfect for a car guard. It's <strong>brand-damaging</strong> for an architect.
          <br/><br/>
          <strong>Proposal:</strong> 4 card types, set during activation. Each type unlocks different features and hides others. Same QR. Same activation flow. Different positioning. <strong>No upfront price tiers — the card cost just covers print + packaging + hosting.</strong> The business lives in the backend.
        </p>

        <div className="mt-10">
          <div className="label-tag mb-3">PICK A TYPE TO PREVIEW</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {TYPES.map((x) => (
              <TypeCard key={x.id} t={x} selected={x.id === active} onSelect={setActive} />
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <ActivationStep t={t} />
          <FeatureMatrix t={t} />
        </div>

        <div className="mt-8 border-2 border-black p-4 bg-[color:var(--cc-bg-2)]">
          <div className="label-tag text-neutral-500 mb-2">PITCH FOR THE {t.title.toUpperCase()} CARD</div>
          <p className="text-base leading-snug"><Sparkle weight="fill" className="inline mr-1" /> {t.pitch}</p>
        </div>

        <div className="mt-10">
          <div className="label-tag mb-3">SCANNED PROFILE PREVIEW</div>
          <ProfileMockup t={t} />
        </div>

        <div className="mt-12 border-t-2 border-black pt-6">
          <h2 className="font-display font-black text-3xl mb-1">Where the money actually is.</h2>
          <p className="text-sm text-neutral-700 mb-5 max-w-2xl">
            The card sale ≈ break-even (print + sleeve + hosting + domain). The card is the <strong>customer acquisition cost</strong>. Every revenue stream below sits in the backend and runs on the data the card already brings in.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="border-2 border-black p-3 bg-white">
              <div className="label-tag">Asset QR stickers</div>
              <div className="text-sm mt-1">Pro + Business cardholders order secondary stickers per asset (geyser, listing, vehicle, equipment). Recurring sticker re-orders + per-scan lead routing fees.</div>
            </div>
            <div className="border-2 border-black p-3 bg-white">
              <div className="label-tag">Lead delivery</div>
              <div className="text-sm mt-1">First N leads free. Volume cardholders pay per lead delivered. Email + WhatsApp templates already shipped — meter it.</div>
            </div>
            <div className="border-2 border-black p-3 bg-white">
              <div className="label-tag">Verified badge</div>
              <div className="text-sm mt-1">Paid identity check on the cardholder (ID + selfie + reference). Trust signal on the public profile. One-off purchase.</div>
            </div>
            <div className="border-2 border-black p-3 bg-white">
              <div className="label-tag">Sponsored Lookup placements</div>
              <div className="text-sm mt-1">Cardholders pay to surface in the public Lookup results for a town / skill combo. Pure backend ranking logic.</div>
            </div>
            <div className="border-2 border-black p-3 bg-white">
              <div className="label-tag">Bulk / corporate orders</div>
              <div className="text-sm mt-1">Residences, retailers, fleets, universities order branded batches. Per-card margin + per-scan analytics fee.</div>
            </div>
            <div className="border-2 border-black p-3 bg-white">
              <div className="label-tag">Premium analytics</div>
              <div className="text-sm mt-1">Heatmaps, hourly trends, lead conversion ratios. Cardholders pay monthly for the dashboard upgrade.</div>
            </div>
            <div className="border-2 border-black p-3 bg-white">
              <div className="label-tag">Reactivation / replacement</div>
              <div className="text-sm mt-1">Lost card? Pay a small fee to bind a fresh QR to the existing profile + leads + reviews. Friction → cash.</div>
            </div>
            <div className="border-2 border-black p-3 bg-white">
              <div className="label-tag">WhatsApp re-engagement</div>
              <div className="text-sm mt-1">Cardholder broadcasts to their lead list ("Specials this week"). Paid credits per outbound message.</div>
            </div>
            <div className="border-2 border-black p-3 bg-white">
              <div className="label-tag">White-label / API</div>
              <div className="text-sm mt-1">Other platforms query a Curb Crew API to verify a service provider exists + is active. B2B SaaS layer on the same data.</div>
            </div>
          </div>

          <h2 className="font-display font-black text-2xl mt-10 mb-3">Build scope (smaller, honest)</h2>
          <ol className="space-y-3 text-sm">
            <li><strong>Phase 1 — Card types (~5 credits):</strong> Add <code>card_type</code> field. New activation step. Conditionally show/hide Quick-Pay, "Send money", portfolio, "Book a meeting" CTA. <em>No pricing logic.</em></li>
            <li><strong>Phase 2 — Asset QRs (~10 credits):</strong> <code>assets</code> collection. Dashboard tab to mint + label secondary QR stickers. Public <code>/a/&#123;asset_code&#125;</code> route routes scans/leads to owner. Print sticker sheets.</li>
            <li><strong>Phase 3 — Lead metering (~3 credits):</strong> Add a counter to <code>leads</code>. Show "X of Y free leads used this month" on cardholder dashboard. <em>No charging yet — just instrument it.</em></li>
          </ol>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/" className="label-tag px-4 py-2 bg-black text-white border-2 border-black brutal-btn">Back home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
